import { type PropsWithChildren, useState } from 'react';
import { Link } from 'react-router';

// Import constants
import { APP_STORE_URL, PLAY_STORE_URL } from '../../constants/links';

// Import utils
import { scrollToId } from '../../utils/scroll';

// Import components
import Badge from '../badge/Badge';
import PremiumUpgradeModal from './PremiumUpgradeModal';

type BillingPeriod = 'monthly' | 'yearly';

type PlanFeatureProps = PropsWithChildren;

function PlanFeature({ children }: PlanFeatureProps) {
  return (
    <li className="flex items-center gap-2 text-md text-gray-700">
      <div className="flex">
        <span className="h-4 w-4 rounded-2xl border border-green-600 bg-green-50 flex-shrink-0" />
      </div>
      <p className="text-start text-sm sm:text-base">{children}</p>
    </li>
  );
}

export default function Pricing() {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [isPremiumModalOpen, setIsPremiumModalOpen] = useState(false);

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 bg-white" id="pricing">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-2">
          Pricing
        </h2>
        <p className="text-gray-600 text-md text-sm sm:text-base text-center max-w-2xl mx-auto mb-8">
          Start for free. Upgrade to Premium anytime to remove ads and enjoy a
          cleaner, faster experience on the{' '}
          <Link
            to="/app"
            className="text-gray-600 hover:text-gray-700 hover:underline font-semibold">
            WhenWeMeet app
          </Link>
          , available on the{' '}
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-gray-700 hover:underline font-semibold">
            App Store
          </a>{' '}
          and{' '}
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-600 hover:text-gray-700 hover:underline font-semibold">
            Google Play
          </a>
          .
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-4 items-stretch">
          {/* Basic plan */}
          <div className="flex flex-col rounded-3xl border border-gray-200 bg-white px-6 py-7 sm:px-8 sm:py-8 shadow-lg hover:shadow-xl transition-shadow">
            <div className="mb-4">
              <p className="text-md font-semibold text-gray-900">Basic</p>
              <p className="mt-1 text-3xl font-bold tracking-tight">$0</p>
              <p className="mt-1 text-md text-gray-500">Free Plan</p>
            </div>

            <ul className="mt-4 space-y-2">
              <PlanFeature>
                Plan and share group availability with meeting planners.
              </PlanFeature>
              <PlanFeature>
                Invite people you want to join your meeting planner.
              </PlanFeature>
              <PlanFeature>
                Add your final meeting time to Google Calendar, Outlook
                Calendar, or download as an ICS file.
              </PlanFeature>
              <PlanFeature>
                All core scheduling features, completely free.
              </PlanFeature>
              <PlanFeature>
                Up to 10 participants per meeting planner.
              </PlanFeature>
            </ul>

            <div className="mt-auto pt-6">
              <button
                type="button"
                className="w-full rounded-full border border-gray-300 bg-white px-4 py-2.5 text-md font-semibold text-gray-900 hover:bg-gray-50"
                onClick={() => scrollToId('hero')}>
                Continue with Free Plan
              </button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                No sign-up required. Get started instantly.
              </p>
            </div>
          </div>

          {/* Premium plan */}
          <div className="relative flex flex-col rounded-3xl border-2 border-black bg-white px-6 py-7 sm:px-8 sm:py-8 shadow-lg hover:shadow-xl transition-shadow">
            {/* Most Popular */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <Badge variant="info" className="text-xs lg:text-base">
                Highly Recommended
              </Badge>
            </div>

            <div className="mb-4">
              <p className="text-md font-semibold text-gray-900">Premium</p>
              {billingPeriod === 'monthly' ? (
                <>
                  <p className="mt-1 text-3xl font-bold tracking-tight">
                    $2.99
                  </p>
                  <p className="mt-1 text-md text-gray-500">
                    per month, billed monthly
                  </p>
                </>
              ) : (
                <>
                  <p className="mt-1 text-3xl font-bold tracking-tight">
                    $29.99
                  </p>
                  <p className="mt-1 text-md text-gray-500">
                    per year, billed annually
                  </p>
                </>
              )}
            </div>

            {/* Billing switcher */}
            <div className="mt-4 grid grid-cols-2 gap-2 rounded-2xl bg-gray-50 p-1 text-md">
              <button
                type="button"
                onClick={() => setBillingPeriod('monthly')}
                className={`flex flex-col rounded-xl px-3 py-2 text-left transition ${
                  billingPeriod === 'monthly'
                    ? 'border border-green-600 bg-white'
                    : 'border border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">Monthly</span>
                  {billingPeriod === 'monthly' && (
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-green-600">
                      <span className="h-2 w-2 rounded-full bg-white" />
                    </span>
                  )}
                </div>
                <span className="mt-1 text-md text-gray-600">
                  $2.99 / month
                </span>
                <span className="mt-0.5 text-md text-gray-500">
                  Billed monthly
                </span>
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('yearly')}
                className={`relative flex flex-col rounded-xl p-3 py-2 text-left transition ${
                  billingPeriod === 'yearly'
                    ? 'border border-green-600 bg-white'
                    : 'border border-gray-200 bg-gray-50 hover:bg-gray-100'
                }`}>
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge
                    variant="secondary"
                    size="small"
                    className="text-xs lg:text-base">
                    16% off
                  </Badge>
                </div>
                <span className="font-medium text-gray-900">Yearly</span>
                <span className="mt-1 text-md text-gray-600">
                  $29.99 / year
                </span>
                <span className="mt-0.5 text-md text-gray-500">
                  Billed annually
                </span>
              </button>
            </div>

            <ul className="mt-5 space-y-2">
              <PlanFeature>Enjoy the mobile app with no ads.</PlanFeature>
              <PlanFeature>
                Cleaner, distraction‑free interface for focused planning.
              </PlanFeature>
              <PlanFeature>
                Support ongoing development and future improvements.
              </PlanFeature>
              <PlanFeature>
                Up to 20 participants per meeting planner.
              </PlanFeature>
            </ul>

            <div className="mt-auto pt-6">
              <button
                type="button"
                onClick={() => setIsPremiumModalOpen(true)}
                className="w-full rounded-full bg-green-600 px-4 py-2.5 text-md font-semibold text-white shadow-sm hover:bg-green-700">
                <span className="flex items-center justify-center gap-2">
                  <span>Upgrade to Premium</span>
                </span>
              </button>
              <p className="mt-2 text-xs text-gray-500 text-center">
                Mobile in‑app pricing may vary slightly by region.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <PremiumUpgradeModal
        isOpen={isPremiumModalOpen}
        onClose={() => setIsPremiumModalOpen(false)}
      />
    </section>
  );
}
